import { useState, useMemo, useRef, useEffect } from 'react';
import { Button, Textbox, Spacing, HalfSpacing, RadioGroup, RadioOption, RoundIconButton, Checkbox, Dropdown, DropdownOption } from '@/form/FormComponents'
import { FormProvider, FormDisabled, FormSubmitProvider, FormDataProvider } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useErrorModal, useModal, ModalListItem } from '@/components/Modal';
import { Icon } from '@/components/Icon';
import { publicAction } from '@/data/loaders';
import { ShareBox } from '@/form/ShareBox';
import { InlineHeader, InlineSection } from '@/components/DashboardComponents';
import { useUser } from '@/systems/Authentication';
import { useMenu } from '@/components/Menu';
import { truncateString } from '@/shared/utils';
import { useRevalidator } from 'react-router';
import { Spinner } from '@/components/Spinner';
import { useImmer } from 'use-immer';

export const useShareModal = ({ setShareLoading, refreshOnShareTypeChange = false } = {}) => {
  const [openErrorModal] = useErrorModal();
  const revalidator = useRevalidator();

  const [openShareModal] = useModal(({ context, closeModal, setLoading }) => {
    return <ShareModal context={context} closeModal={closeModal} setLoading={setLoading} revalidator={revalidator} refreshOnShareTypeChange={refreshOnShareTypeChange}/>
  }, [])

  const handleOpenShareModal = (file) => {
    setShareLoading?.(true);
    publicAction("getFileSharing", { id: file._id, include_rubric_check: file.type === "scenario" }).then(function({data}) {
      setShareLoading?.(false);
      openShareModal({
        title: file.title,
        type: file.type,
        id: file._id,
        ...data
      })
    }).catch(function(error) {
      setShareLoading?.(false);
      console.error(error);
      openErrorModal();
    });
  }

  return [handleOpenShareModal];
}

export const useShareRubricsModal = ({ wording = "share_modal" }) => {
  const [openShareRubricsModal] = useModal(({ context, closeModal, setLoading }) => {
    const { share_prompt_rubrics, callback } = context;

    const handleSubmit = function({ formatted_data }) {
      if (share_prompt_rubrics.length > 1) {
        callback({
          set_should_share_rubrics: share_prompt_rubrics.map(rubric => ({ ...rubric, should_share: formatted_data[rubric.file_id] })),
          closeModal,
          setLoading
        });
      } else {
        callback({
          set_should_share_rubrics: share_prompt_rubrics.map(rubric => ({ ...rubric, should_share: true })),
          closeModal,
          setLoading
        });
      }
    }

    return (
      <Modal title={wording === "scenario_editor" ? (share_prompt_rubrics.length > 1 ? "Allow everyone this scenario is shared with to edit these rubrics?" : "Allow everyone this scenario is shared with to edit this rubric?") : (share_prompt_rubrics.length > 1 ? "Also allow editing these rubrics?" : "Also allow editing this rubric?")} width={400}>
        <FormProvider onSubmit={handleSubmit}>
          <ModalContent>
            {share_prompt_rubrics.length > 1 ? (
              <>
                {wording === "scenario_editor" ? (
                  <span>{"Choose whether to share "}<b style={{fontWeight: "500"}}>edit</b>{" access to these linked rubric files:"}</span>
                ) : (
                  <span>{"Choose whether to also share "}<b style={{fontWeight: "500"}}>edit</b>{" access to these linked rubric files:"}</span>
                )}
                <Spacing/>
                {share_prompt_rubrics.map(rubric => (
                  <Checkbox key={rubric.file_id} name={rubric.file_id} text={<div className="share_file_pill"><Icon name="bullseye-arrow" style={{fill: "var(--rubric_file_color)"}}/><span>{rubric.title}</span></div>} default_value={true} ariaLabel={rubric.title} />
                ))}
              </>
            ) : (
              <>
                {wording === "scenario_editor" ? (
                  <span>{"Choose whether to share "}<b style={{fontWeight: "500"}}>edit</b>{" access to this linked rubric file:"}</span>
                ) : (
                  <span>{"Choose whether to also share "}<b style={{fontWeight: "500"}}>edit</b>{" access to this linked rubric file:"}</span>
                )}
                <Spacing/>
                <div style={{display: "flex"}}>
                  <div className="share_file_pill"><Icon name="bullseye-arrow" style={{fill: "var(--rubric_file_color)"}}/><span>{share_prompt_rubrics[0].title}</span></div>
                </div>
              </>
            )}
          </ModalContent>
          <ModalButtons>
            <FormSubmitProvider>
              {({ handleSubmit }) => (
                <>
                  <Button text={`No thanks`} onClick={() => {
                    callback({
                      set_should_share_rubrics: share_prompt_rubrics.map(rubric => ({ ...rubric, should_share: false })),
                      closeModal,
                      setLoading
                    });
                  }}/>
                  {wording === "scenario_editor" ? (
                    <Button text={`Share edit access`} onClick={handleSubmit}/>
                  ) : (
                    <Button text={`Allow editing rubric${share_prompt_rubrics.length > 1 ? "s" : ""}`} onClick={handleSubmit}/>
                  )}
                </>
              )}
            </FormSubmitProvider>
          </ModalButtons>
        </FormProvider>
      </Modal>
    )
  })

  return [openShareRubricsModal];
}

function ShareModal({ context, closeModal, setLoading, revalidator, refreshOnShareTypeChange = false }) {
  const { uid, userdata } = useUser();
  const user_organization_id = userdata.organization_id;
  const { title, type, id, access_list: access_list_initial, owner_organization_id, owner_organization_name, owner_organization_shortname, should_share_rubrics, share_prompt_condition, share_prompt_rubrics } = context;
  const [access_list, updateAccessList] = useImmer(access_list_initial);
  const [access_list_loading, updateAccessListLoading] = useImmer({});

  const share_type = (access_list.some(entry => entry.organization_id === "all") && refreshOnShareTypeChange) ? "public" : (access_list.some(entry => entry.organization_id) && refreshOnShareTypeChange) ? "organization" : (access_list.filter(entry => entry.level !== "owner").length > 0 ? "people" : null);
  const initial_share_type = useRef(share_type);

  useEffect(() => {
    if (share_type !== initial_share_type.current) {
      initial_share_type.current = share_type;
      revalidator.revalidate();
    }
  }, [share_type, revalidator]);

  const [openShareRubricsModal] = useShareRubricsModal({});

  function submitForm({ formatted_data, focusError }) {

    if (share_prompt_condition != null && share_prompt_rubrics?.length > 0 && formatted_data.level === "edit" && !(!formatted_data.share_rubrics && should_share_rubrics?.length > 0)) {
      const has_people_not_in_share_prompt_condition = formatted_data.people.some(person => !share_prompt_condition.some(entry => person._id ? entry._id === person._id : entry.email === person.email));
      if (has_people_not_in_share_prompt_condition) {
        openShareRubricsModal({
          share_prompt_rubrics,
          callback: ({ set_should_share_rubrics, closeModal }) => {
            closeModal();
            doShareFile({ set_should_share_rubrics });
          }
        });
      } else {
        doShareFile({});
      }
    } else {
      doShareFile({});
    }

    function doShareFile({ set_should_share_rubrics }) {
      setLoading(true);

      publicAction("shareFiles", {
        ids: [id, ...((formatted_data.share_rubrics && should_share_rubrics?.length > 0) ? should_share_rubrics.map(rubric => rubric.file_id) : []), ...(set_should_share_rubrics || []).filter(rubric => rubric.should_share).map(rubric => rubric.file_id)],
        people: formatted_data.people,
        level: formatted_data.level,
        notify_people: formatted_data.notify_people,
        message: formatted_data.message,
        set_should_share_rubrics: (set_should_share_rubrics || []).map(rubric => ({
          _id: rubric._id,
          should_share: rubric.should_share,
        })),
      }).then(function() {
  
        closeModal();
        if (!share_type) {
          revalidator.revalidate();
        }
  
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "people", error: "Something went wrong, please try again."});
      })
    }
  }

  const sorted_people_with_access = useMemo(() => access_list
    .filter(entry => !entry.organization_id)
    .sort((a, b) => {
      // Owner first
      if (a.level === 'owner') return -1;
      if (b.level === 'owner') return 1;
      
      // Current user second
      if (a.uid === uid) return -1;
      if (b.uid === uid) return 1;
      
      // Users with uid next, sorted by name
      if (a.uid && b.uid) {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (a.uid) return -1;
      if (b.uid) return 1;
      
      // Remaining entries (courses) sorted by name
      return (a.name || '').localeCompare(b.name || '');
    }), [access_list, uid]);

  const sorted_organizations_with_access = useMemo(() => access_list
    .filter(entry => entry.organization_id)
    .sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    }), [access_list]);

  return (
    <FormProvider onSubmit={submitForm}>
      <FormDataProvider fields={["people", "notify_people", "level"]}>
        {({ data, updateData }) => (
          <Modal title={`Share "${truncateString(title, 100)}"`} onBack={data.people?.length > 0 ? () => {updateData(draft => {draft.people = []});} : undefined}>
            <ModalContent>
              <ShareBox name="people" placeholder="Add emails or names" required={"Please add at least one person."}>
                {data.people?.length > 0 && (
                  <Dropdown name="level" default_value="edit" required={true}>
                    <DropdownOption name="view" text="Viewer"/>
                    <DropdownOption name="edit" text="Editor"/>
                  </Dropdown>
                )}
              </ShareBox>
              <div style={{display: !(data.people?.length > 0) ? "contents" : "none"}}>
                <Spacing/>
                <InlineHeader title="People with access" small={true} />
                <InlineSection>
                  {sorted_people_with_access.map(entry => (
                    <AccessItem key={entry.uid ? `uid:${entry.uid}` : entry.course_id ? `course:${entry.course_id}` : `organization:${entry.organization_id}`} entry={entry} user_uid={uid} user_organization_id={user_organization_id} title={title} type={type} id={id} loading={access_list_loading[entry.uid ? `uid:${entry.uid}` : entry.course_id ? `course:${entry.course_id}` : `organization:${entry.organization_id}`]} updateAccessListLoading={updateAccessListLoading} updateAccessList={updateAccessList} />
                  ))}
                </InlineSection>
                <InlineHeader title="General access" small={true} />
                <InlineSection halfMarginBottom={true}>
                  <GeneralAccess entries={sorted_organizations_with_access} user_uid={uid} user_organization_id={user_organization_id} title={title} type={type} id={id} access_list_loading={access_list_loading} updateAccessListLoading={updateAccessListLoading} updateAccessList={updateAccessList} owner_organization_id={owner_organization_id} owner_organization_name={owner_organization_name} owner_organization_shortname={owner_organization_shortname} />
                </InlineSection>
              </div>
              <div style={{display: data.people?.length > 0 ? "contents" : "none"}}>
                <HalfSpacing/>
                {should_share_rubrics.length > 0 && data.level === "edit" && (
                  <Checkbox name="share_rubrics" text={`Also share edit access to ${should_share_rubrics.length} linked rubric file${should_share_rubrics.length > 1 ? "s" : ""}`} default_value={true} />
                )}
                <Checkbox name="notify_people" text="Notify people" default_value={true} />
                <HalfSpacing/>
                <FormDisabled obviated={!data.notify_people}>
                  <Textbox name="message" placeholder="Message" multiline={true} height={3} autofocus={data.notify_people} />
                </FormDisabled>
              </div>
            </ModalContent>
            <FormSubmitProvider>
              {({ handleSubmit, has_changed }) => (
                <>
                  {!(data.people?.length > 0) && <ModalButtons space_between={true}>
                    <CopyAccessLinkButton type={type} id={id} />
                    <Button text="Done" className="" onClick={closeModal} />
                  </ModalButtons>}
                  {(data.people?.length > 0) && <ModalButtons>
                    <Button text="Cancel" className="hollow" onClick={() => {updateData(draft => {draft.people = []});}} />
                    <Button text="Share" className="primary wide" onClick={handleSubmit} disabled={!has_changed} />
                  </ModalButtons>}
                </>
              )}
            </FormSubmitProvider>
          </Modal>
        )}
      </FormDataProvider>
    </FormProvider>
  )
}

function CopyAccessLinkButton({ type, id }) {
  const [justCopied, setJustCopied] = useState(false);
  const resetCopiedTimeout = useRef(null);

  const copyText = function() {
    navigator.clipboard.writeText(`https://sapientclinician.com/${type}s/${id}`);
    setJustCopied(true);

    if (resetCopiedTimeout.current) {
      clearTimeout(resetCopiedTimeout.current);
    }

    resetCopiedTimeout.current = setTimeout(() => {
      setJustCopied(false);
    }, 1500);
  }

  return <Button text={justCopied ? "Copied" : "Copy link"} icon={justCopied ? "check" : "link"} className="hollow" onClick={copyText} />;
}

function AccessItem({ entry, user_uid, user_organization_id, title, type, id, loading, updateAccessListLoading, updateAccessList, dropdown, owner_organization_id, owner_organization_name, owner_organization_shortname }) {
  const { uid, course_id, organization_id, email, name, shortname, level, levels } = entry;
  
  function setLoading(new_loading) {
    updateAccessListLoading(draft => {
      draft[entry.uid ? `uid:${entry.uid}` : entry.course_id ? `course:${entry.course_id}` : `organization:${entry.organization_id}`] = new_loading;
    });
  }

  const levelOrder = ["view", "edit"];

  function setLevel(new_level) {
    updateAccessList(draft => {
      let entry = draft.find(e => (uid && e.uid === uid) || (course_id && e.course_id === course_id) || (organization_id && e.organization_id === organization_id));
      if (new_level) {
        entry.level = new_level;
        // Remove via_parent so it doesn't bring up the popup again if raising and lowering again
        if (levelOrder.indexOf(new_level) < levelOrder.indexOf(level)) {
          entry.levels = entry.levels.map(l => {
            if (levelOrder.indexOf(l.level) >= levelOrder.indexOf(new_level)) {
              return { ...l, via_parent: undefined };
            } else {
              return l;
            }
          });
        }
      } else {
        return draft.filter(e => e !== entry);
      }
    });
  }

  const [openErrorModal] = useErrorModal();

  function updateFileSharing({ new_level }) {
    setLoading(true);

    publicAction("updateFileSharing", {
      id: id,
      uid: uid,
      course_id: course_id,
      organization_id: organization_id,
      level: new_level
    }).then(function() {
      setLoading(false);
      setLevel(new_level);
    }).catch(function(error) {
      console.error(error);
      setLoading(false);
      openErrorModal();
    });
  }

  const [openRemoveFromParentModal] = useModal(({ context, closeModal }) => {
    const { title: parent_title, old_level: parent_old_level, new_level, far_apart } = context;
    
    function handleSubmit() {
      closeModal();
      updateFileSharing({ new_level });
    }

    const level_names = {
      view: "Viewer",
      edit: "Editor",
      owner: "Owner",
      null: "Remove",
    }

    const person_name = uid ? email : (course_id ? `${name} course instructors` : organization_id ? (organization_id === "all" ? "Anyone with the link" : name) : null);

    return (
      <Modal title={new_level ? "Update role on parent folder?" : "Remove from parent folder?"}>
        <ModalContent>
          {new_level ? <span>{`Changing `}<b style={{fontWeight: "500"}}>{person_name}</b>{`'s access to this ${type} will also change their access to a parent folder.`}</span> : <span>{`Removing `}<b style={{fontWeight: "500"}}>{person_name}</b>{` from this ${type} will also remove them from a parent folder.`}</span>}
          <div className="parent_folder_graphic">
            <div>
              <div>
                <Icon name="folder-account"/>
                <div></div>
                {far_apart && <div><Icon name="dots-horizontal"/></div>}
              </div>
              <div>
                <Icon name={{folder: "folder-account", scenario: "forum-outline", rubric: "bullseye-arrow"}[type]} style={{fill: {folder: "", scenario: "var(--scenario_file_color)", rubric: "var(--rubric_file_color)"}[type]}}/>
              </div>
            </div>
            <div>
              <div>
                <div>{parent_title}</div>
                <div><span>{level_names[parent_old_level]}</span><Icon name="arrow-right-alt"/><span>{level_names[new_level]}</span></div>
              </div>
              <div>
                <div>{title}</div>
                <div><span>{level_names[level]}</span><Icon name="arrow-right-alt"/><span>{level_names[new_level]}</span></div>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text={new_level ? "Update parent" : "Remove from parent"} className={new_level ? "primary" : "destructive"} onClick={handleSubmit}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleChangeLevel = (new_level) => {
    if (new_level === level) return;

    const { via_parent } = levels.find(l => l.level === new_level);
    if (via_parent) {
      openRemoveFromParentModal({ ...via_parent, new_level });
    } else {
      updateFileSharing({ new_level });
    }
  }

  const { handleOnClick } = useMenu({
    menuItems: [
      {
        label: "Viewer",
        icon: level === "view" ? "check" : "empty",
        disabled: levels.find(l => l.level === "view")?.eligible !== true,
        onClick: () => {handleChangeLevel("view")},
      },
      {
        label: "Editor",
        icon: level === "edit" ? "check" : "empty",
        disabled: levels.find(l => l.level === "edit")?.eligible !== true,
        onClick: () => {handleChangeLevel("edit")},
      },
      "separator",
      {
        label: "Remove access",
        disabled: levels.find(l => l.level === null)?.eligible !== true,
        onClick: () => {handleChangeLevel(null)},
      }
    ]
  });

  const disabled = !levels.some(l => l.eligible && l.level !== level);

  const { handleOnClick: handleOnClickOrganization } = useMenu({
    menuItems: [
      {
        label: organization_id === "all" ? "Anyone with the link" : ((organization_id === user_organization_id ? shortname : name) || "(Deleted organization)"),
        icon: "check",
        onClick: () => {},
      },
      "separator",
      {
        label: "Remove access",
        disabled: levels.find(l => l.level === null)?.eligible !== true,
        onClick: () => {handleChangeLevel(null)},
      }
    ],
    rightHanded: false
  });

  const can_remove = levels.find(l => l.level === null)?.eligible === true;
  const remove_has_via = levels.find(l => l.level === null)?.via_parent;

  return (
    <div className="person_item small">
      {!organization_id ? (
        <div className="person_item_icon">
          <Icon name={uid ? (uid.charAt(0) != "_" ? "account-circle-outline" : "email-outline") : "book-open-variant-outline"}/>
        </div>
      ) : (
        <div className={"person_item_icon with_background access_"+(organization_id === "all" ? "public" : "organization")}>
          <Icon name={(organization_id === "all" ? "public" : "domain")}/>
        </div>
      )}
      <div className={"person_item_text" + (organization_id ? " share_general_access" : "")}>
        {uid ? (
          <>
            {uid.charAt(0) != "_" ? <div>{(name || (email ? "(No name)" : "(Deleted user)")) + (uid === user_uid ? " (you)" : "")}</div> : null}
            <div>{email}</div>
            {uid.charAt(0) == "_" ? <div>Pending invitation</div> : null}
          </>
        ) : (
          course_id ? (
            <>
              <div>{name || "(Deleted course)"}</div>
              <div>Course instructors</div>
            </>
          ) : (
            <>
              <div>
                {dropdown ? (
                  <GeneralAccessDropdown organization_id={organization_id} current_level={level} can_remove={can_remove} handleRemove={() => {handleChangeLevel(null)}} remove_has_via={remove_has_via} loading={loading} setLoading={setLoading} id={id} user_organization_id={user_organization_id} owner_organization_id={owner_organization_id} owner_organization_name={owner_organization_shortname} owner_organization_shortname={owner_organization_shortname} updateAccessList={updateAccessList} />
                ) : (
                  <button
                    type="button"
                    onClick={handleOnClickOrganization}
                    className={"share_dropdown_button general_access" + (loading ? " disabled" : "")}
                    disabled={loading}
                  >
                    <div>
                      {organization_id === "all" ? "Anyone with the link" : ((organization_id === user_organization_id ? shortname : name) || "(Deleted organization)")}
                    </div>
                    <div>
                      <Icon name="menu-down-enlarged"/>
                    </div>
                  </button>
                )}
              </div>
              <div>{organization_id === "all" ? <>{`Anyone on the internet with the link can ${level} `}<nobr>{`(sign in required)`}</nobr></> : `Anyone in this organization with the link can ${level}`}</div>
            </>
          )
        )}
      </div>
      <div className="person_item_more access_item_more">
        {level === "owner" ? (
          <div className="access_owner_label">Owner</div>
        ) : (
          loading ? (
            <Spinner className="share_dropdown_spinner"/>
          ) : (
            <button
              type="button"
              onClick={handleOnClick}
              className={"share_dropdown_button" + (disabled ? " disabled" : "")}
              disabled={disabled}
            >
              <div>
                {level === "view" ? "Viewer" : "Editor"}
              </div>
              <div>
                <Icon name="menu-down-enlarged"/>
              </div>
            </button>
          )
        )}
      </div>
    </div>
  )
}

function GeneralAccess({ entries, user_uid, user_organization_id, title, type, id, access_list_loading, updateAccessListLoading, updateAccessList, owner_organization_id, owner_organization_name, owner_organization_shortname }) {

  const org_entry = entries.find(entry => entry.organization_id === owner_organization_id);
  const all_entry = entries.find(entry => entry.organization_id === "all");
  const other_entries = entries.filter(entry => entry.organization_id !== owner_organization_id && entry.organization_id !== "all");

  const common_props = { user_uid, user_organization_id, title, type, id, updateAccessListLoading, updateAccessList, owner_organization_id, owner_organization_name, owner_organization_shortname }

  if (entries.length === 0) {
    // 3 option dropdown, set to "restricted"
    return <RestrictedAccess id={id} {...common_props} />
  } else {
    if (all_entry) {
      if (org_entry) {
        // individual entries with remove option
        return entries.map(entry => (
          <AccessItem key={entry.organization_id} entry={entry} loading={access_list_loading[`organization:${entry.organization_id}`]} {...common_props} />
        ))
      } else {
        // 3 opts, all (restricted and org options may be grayed out)
        // + any additional orgs w/ remove
        return <>
          <AccessItem dropdown={true} entry={all_entry} loading={access_list_loading[`organization:${all_entry.organization_id}`]} {...common_props} />
          {other_entries.map(entry => (
            <AccessItem key={entry.organization_id} entry={entry} loading={access_list_loading[`organization:${entry.organization_id}`]} {...common_props} />
          ))}
        </>
      }
    } else {
      if (org_entry) {
        // 3 opts, org (restricted may be grayed out, but setting to all is always allowed)
        // + any additional orgs w/ remove
        return <>
          <AccessItem dropdown={true} entry={org_entry} loading={access_list_loading[`organization:${org_entry.organization_id}`]} {...common_props} />
          {other_entries.map(entry => (
            <AccessItem key={entry.organization_id} entry={entry} loading={access_list_loading[`organization:${entry.organization_id}`]} {...common_props} />
          ))}
        </>
      } else {
        // 3 opts, restricted
        // + any additional orgs w/ remove
        return <>
          <RestrictedAccess id={id} {...common_props} />
          {other_entries.map(entry => (
            <AccessItem key={entry.organization_id} entry={entry} loading={access_list_loading[`organization:${entry.organization_id}`]} {...common_props} />
          ))}
        </>
      }
    }
  }
}

function GeneralAccessDropdown({ organization_id, can_remove, remove_has_via, handleRemove, loading, setLoading, id, user_organization_id, owner_organization_id, owner_organization_name, owner_organization_shortname, updateAccessList, current_level }) {
  const [openErrorModal] = useErrorModal();

  function addLinkSharing({ new_organization_id, level }) {
    setLoading(true);

    const new_entry = {
      organization_id: new_organization_id,
      ...(new_organization_id === owner_organization_id ? { name: owner_organization_name, shortname: owner_organization_shortname } : {}),
      level: level,
      levels: [{
        level: "view",
        eligible: true,
      }, {
        level: "edit",
        eligible: true,
      }, {
        level: null,
        eligible: true,
      }]
    }

    // Already has org access, and we can remove the old one directly (doesn't have via_parent)
    // Example: Has org access, and we're switching to link access. (remove old and add new)
    // Counterexample: Parent has org access, and we chose link access (will simply ADD link access instead of attempting to remove org access from parent)
    if (organization_id && !remove_has_via) {
      // Remove the old one first

      publicAction("updateFileSharing", {
        id: id,
        organization_id: organization_id,
        level: null
      }).then(function() {
        
        publicAction("updateFileSharing", {
          id: id,
          organization_id: new_organization_id,
          level: current_level
        }).then(function() {
          
          updateAccessList(draft => {
            return [...draft.filter(e => e.organization_id !== organization_id), {...new_entry, level: current_level}];
          });
          setLoading(false);
    
        }).catch(function(error) {
          console.error(error);
          setLoading(false);
          updateAccessList(draft => {
            return [...draft.filter(e => e.organization_id !== organization_id)];
          });
          openErrorModal();
        });
  
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        openErrorModal();
      });
    } else {
      publicAction("updateFileSharing", {
        id: id,
        organization_id: new_organization_id,
        level: level
      }).then(function() {
        
        updateAccessList(draft => {
          return [...draft, new_entry];
        });
        setLoading(false);
  
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        openErrorModal();
      });
    }
  }

  const { handleOnClick: handleOnClickOrganization } = useMenu({
    menuItems: [
      {
        label: "Restricted",
        icon: !organization_id ? "check" : "empty",
        onClick: handleRemove,
        disabled: !can_remove,
      },
      ...(owner_organization_id ? [{
        label: user_organization_id === owner_organization_id ? owner_organization_shortname : owner_organization_name,
        icon: (organization_id && organization_id !== "all") ? "check" : "empty",
        onClick: () => {
          if (!(organization_id && organization_id !== "all")) {
            addLinkSharing({ new_organization_id: owner_organization_id, level: "view" });
          }
        },
        disabled: organization_id === "all" && !can_remove,
      }] : []),
      {
        label: "Anyone with the link",
        icon: organization_id === "all" ? "check" : "empty",
        onClick: () => {
          if (organization_id !== "all") {
            addLinkSharing({ new_organization_id: "all", level: "view" });
          }
        },
      }
    ],
    rightHanded: false
  });

  return (
    <button
      type="button"
      onClick={handleOnClickOrganization}
      className={"share_dropdown_button general_access" + (loading ? " disabled" : "")}
      disabled={loading}
    >
      <div>
        {organization_id ? (organization_id === "all" ? "Anyone with the link" : owner_organization_shortname) : "Restricted"}
      </div>
      <div>
        <Icon name="menu-down-enlarged"/>
      </div>
    </button>
  )
}

function RestrictedAccess({ id, user_organization_id, owner_organization_id, owner_organization_name, owner_organization_shortname, updateAccessList }) {
  const [loading, setLoading] = useState(false);

  return (
    <div className="person_item small">
      <div className={"person_item_icon with_background access_"+"restricted"}>
        <Icon name={"lock-outline"}/>
      </div>
      <div className="person_item_text share_general_access">
        <div>
          <GeneralAccessDropdown organization_id={null} can_remove={true} loading={loading} setLoading={setLoading} id={id} user_organization_id={user_organization_id} owner_organization_id={owner_organization_id} owner_organization_name={owner_organization_name} owner_organization_shortname={owner_organization_shortname} updateAccessList={updateAccessList} />
        </div>
        <div>{"Only people with access can open with the link"}</div>
      </div>
      <div className="person_item_more access_item_more">
        {loading && <Spinner className="share_dropdown_spinner"/>}
      </div>
    </div>
  )
}